import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Select, DatePicker, message } from 'antd';
import dayjs from 'dayjs';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  ArrowLeftIcon,
  CreateTaskIcon,
  ImagePlaceholderIcon,
  TrashIcon,
  PlusIcon,
} from '../../assets/icons';
import { useUploadS3Mutation } from '../../features/uploads/uploadApi';
import {
  useGetCoordinatorsQuery,
  useGetTaskQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  coordinatorName,
  taskTitle,
  taskDesc,
  taskTags,
  taskDate,
  taskRawStatus,
  taskAssigneeId,
  statusKey,
  API_STATUS,
  type UiTaskStatus,
} from '../../features/tasks/tasksApi';

// Filled (#eef4ff) field look for this screen — see `.tf-field` / `.tf-select` in index.css.
const FIELD = 'tf-field w-full rounded-lg px-4 text-sm text-ink';
const fieldCls = (invalid?: string) => [FIELD, invalid ? 'tf-invalid' : ''].join(' ');
const TEXTAREA = 'tf-field w-full resize-none rounded-lg px-4 py-3 text-sm text-ink';
const ERR = 'text-xs font-medium text-[#ba1a1a]';

const STATUSES = [
  { value: 'planning', label: 'Planning' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'inprogress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

interface TaskAttachmentValue {
  file_url: string;
  file_type: string;
  name: string;
}

interface TaskFormValues {
  name: string;
  coordinator: number | '';
  tags: string[];
  status: string;
  dueDate: string;
  description: string;
  attachments: TaskAttachmentValue[];
}

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10MB, matching the dropzone copy.

/** Short type tag (e.g. "pdf", "png") from a filename or MIME type. */
function fileTypeTag(fileName: string, contentType?: string): string {
  const ext = fileName.includes('.') ? fileName.split('.').pop()!.toLowerCase() : '';
  if (ext) return ext;
  return contentType?.split('/')[1] || 'file';
}

function Label({ children, required }: { children: ReactNode; required?: boolean }) {
  return (
    <span className="text-sm font-medium text-ink">
      {children}
      {required && <span className="text-[#ef4444]"> *</span>}
    </span>
  );
}

export default function CreateTaskPage({ variant = 'task' }: { variant?: 'task' | 'subtask' }) {
  const navigate = useNavigate();
  const { id, taskId, subId } = useParams();
  const location = useLocation();
  const state = location.state as { name?: string; taskName?: string } | null;
  const engagementName = state?.name ?? 'Guru Poornima';
  const taskName = state?.taskName ?? 'Blood Donation Camp';
  const isSub = variant === 'subtask';
  const isEdit = location.pathname.endsWith('/edit');
  // The record being edited: a subtask uses :subId, a task uses :taskId.
  const editingId = isEdit ? (isSub ? subId : taskId) : undefined;

  const engagementPath = `/user/engagements/${id ?? ''}`;
  const taskPath = `/user/engagements/${id ?? ''}/tasks/${taskId ?? ''}`;
  // Where the back arrow / cancel / submit return to.
  const backPath = isSub ? taskPath : engagementPath;
  const backState = isSub ? { name: engagementName, taskName } : { name: engagementName };

  const noun = isSub ? 'Sub Task' : 'Task';
  const title = isEdit ? `Edit ${noun}` : isSub ? 'Sub New Task' : 'New Task';
  const submitLabel = isEdit ? `UPDATE ${noun.toUpperCase()}` : isSub ? 'CREATE SUB TASK' : 'CREATE TASK';

  // Coordinator dropdown — users inside the engagement's boundary.
  const { data: coordinators } = useGetCoordinatorsQuery(id!, { skip: !id });
  const { data: editingTask } = useGetTaskQuery(editingId!, { skip: !editingId });
  const [createTask, { isLoading: isCreating }] = useCreateTaskMutation();
  const [updateTask, { isLoading: isUpdating }] = useUpdateTaskMutation();
  const saving = isCreating || isUpdating;

  const breadcrumbs = isSub
    ? [
        { label: 'Engagements', to: '/user/engagements' },
        { label: engagementName, to: engagementPath },
        { label: taskName, to: taskPath },
        { label: isEdit ? 'Edit Sub Task' : 'Sub New Task' },
      ]
    : [
        { label: 'Engagements', to: '/user/engagements' },
        { label: engagementName, to: engagementPath },
        { label: isEdit ? 'Edit Task' : 'Create New Task' },
      ];

  // Required: name, coordinator, due date.
  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().trim().required('Task name is required'),
        coordinator: Yup.mixed().test(
          'required',
          'Assigned coordinator is required',
          (v) => v !== '' && v != null,
        ),
        dueDate: Yup.string().required('Due date is required'),
      }),
    [],
  );

  const formik = useFormik<TaskFormValues>({
    initialValues: {
      name: '',
      coordinator: '',
      tags: [],
      status: 'planning',
      dueDate: '',
      description: '',
      attachments: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      const payload = {
        task_name: values.name.trim(),
        description: values.description.trim(),
        ...(values.tags.length ? { tags: values.tags } : {}),
        task_status: API_STATUS[values.status as UiTaskStatus] ?? 'Pending',
        ...(values.coordinator !== '' ? { assigned_user: Number(values.coordinator) } : {}),
        ...(values.dueDate ? { scheduled_date: values.dueDate } : {}),
        ...(values.attachments.length
          ? {
              attachments: values.attachments.map((a) => ({
                file_url: a.file_url,
                file_type: a.file_type,
              })),
            }
          : {}),
      };
      try {
        if (isEdit && editingId) {
          await updateTask({ id: editingId, patch: payload }).unwrap();
        } else {
          await createTask({
            ...payload,
            // A subtask carries its parent; a top-level task carries its engagement.
            ...(isSub && taskId ? { parent_task: Number(taskId) } : {}),
            ...(!isSub && id ? { engagement_id: Number(id) } : {}),
          }).unwrap();
        }
        navigate(backPath, { state: backState });
      } catch {
        const verb = isEdit ? 'update' : 'create';
        message.error(`Could not ${verb} the ${isSub ? 'sub task' : 'task'}. Please try again.`);
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched } =
    formik;
  const errorOf = (name: keyof TaskFormValues) =>
    touched[name] && errors[name] ? (errors[name] as string) : undefined;

  // Edit mode: prefill the form from the loaded task/subtask.
  useEffect(() => {
    if (!editingTask) return;
    setFieldValue('name', taskTitle(editingTask) === 'Untitled task' ? '' : taskTitle(editingTask));
    setFieldValue('description', taskDesc(editingTask));
    setFieldValue('tags', taskTags(editingTask));
    setFieldValue('dueDate', taskDate(editingTask));
    const raw = taskRawStatus(editingTask);
    if (raw) setFieldValue('status', statusKey(raw));
    const owner = taskAssigneeId(editingTask);
    if (owner != null) setFieldValue('coordinator', owner);
    setFieldValue(
      'attachments',
      (editingTask.attachments ?? [])
        .filter((a) => a.file_url)
        .map((a) => ({
          file_url: a.file_url!,
          file_type: a.file_type || fileTypeTag(a.file_url!),
          name: a.name || a.file_url!.split('/').pop() || 'attachment',
        })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTask]);

  // Ensure the loaded status is always selectable, even if outside the default set.
  const statusOptions = useMemo(() => {
    if (values.status && !STATUSES.some((o) => o.value === values.status)) {
      return [
        ...STATUSES,
        { value: values.status, label: values.status.charAt(0).toUpperCase() + values.status.slice(1) },
      ];
    }
    return STATUSES;
  }, [values.status]);

  // Tags — type + Add (or Enter) to append; chips are individually removable.
  const [tagInput, setTagInput] = useState('');
  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!values.tags.includes(t)) setFieldValue('tags', [...values.tags, t]);
    setTagInput('');
  };
  const removeTag = (t: string) =>
    setFieldValue('tags', values.tags.filter((x) => x !== t));

  // Upload picked files through the backend (which stores them in S3) and keep
  // the returned URLs to send in the task's attachments payload.
  const [uploadS3] = useUploadS3Mutation();
  const [uploading, setUploading] = useState(false);
  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      const done: TaskAttachmentValue[] = [];
      for (const file of Array.from(fileList)) {
        if (file.size > MAX_UPLOAD_BYTES) {
          message.error(`${file.name} exceeds the 10MB limit.`);
          continue;
        }
        const res = await uploadS3(file).unwrap();
        done.push({ file_url: res.url, file_type: fileTypeTag(file.name, res.content_type), name: file.name });
      }
      if (done.length) setFieldValue('attachments', [...values.attachments, ...done]);
    } catch {
      message.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };
  const removeAttachment = (url: string) =>
    setFieldValue(
      'attachments',
      values.attachments.filter((a) => a.file_url !== url),
    );

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-8">
      {/* Breadcrumb */}
      <Breadcrumbs items={breadcrumbs} />

      {/* Heading */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Back"
            onClick={() => navigate(backPath, { state: backState })}
            className="text-ink transition-colors hover:text-brand"
          >
            <ArrowLeftIcon className="size-7" />
          </button>
          <h1 className="text-2xl font-semibold text-ink">{title}</h1>
        </div>
        <p className="text-base text-ink-secondary">
          Configure details, assign coordinators, and set timelines for the upcoming engagement.
        </p>
      </div>

      {/* Form card */}
      <div className="flex flex-col gap-6 rounded-xl border border-[#f1f5f9] bg-white p-8 shadow-card">
        {/* Task Name | Status */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <Label required>Task Name</Label>
            <input
              name="name"
              placeholder="e.g., Venue finalization and seating arrangement"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldCls(errorOf('name'))}
            />
            {errorOf('name') && <span className={ERR}>{errorOf('name')}</span>}
          </label>
          <div className="flex flex-col gap-1.5">
            <Label>Status</Label>
            <Select
              className="tf-select w-full"
              value={values.status}
              onChange={(v) => setFieldValue('status', v)}
              options={statusOptions}
            />
          </div>
        </div>

        {/* Assigned Coordinator | Due Date */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label required>Assigned Coordinator</Label>
            <Select
              className="tf-select w-full"
              placeholder="Select Coordinator"
              showSearch
              optionFilterProp="label"
              status={errorOf('coordinator') ? 'error' : undefined}
              value={values.coordinator === '' ? undefined : values.coordinator}
              onChange={(v) => setFieldValue('coordinator', v)}
              onBlur={() => setFieldTouched('coordinator', true)}
              options={(coordinators ?? []).map((c) => ({ value: c.id, label: coordinatorName(c) }))}
            />
            {errorOf('coordinator') && <span className={ERR}>{errorOf('coordinator')}</span>}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label required>Due Date</Label>
            <DatePicker
              className="tf-select w-full"
              format="DD/MM/YYYY"
              placeholder="dd/mm/yyyy"
              status={errorOf('dueDate') ? 'error' : undefined}
              value={values.dueDate ? dayjs(values.dueDate) : null}
              onChange={(d) => setFieldValue('dueDate', d ? d.format('YYYY-MM-DD') : '')}
              onBlur={() => setFieldTouched('dueDate', true)}
            />
            {errorOf('dueDate') && <span className={ERR}>{errorOf('dueDate')}</span>}
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-col gap-1.5">
          <Label>Tags</Label>
          <div className="flex items-stretch gap-3">
            <input
              value={tagInput}
              placeholder="Add Tags"
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag();
                }
              }}
              className={[FIELD, 'flex-1'].join(' ')}
            />
            <button
              type="button"
              onClick={addTag}
              className="flex h-11 shrink-0 items-center gap-2 rounded-[10px] border border-brand px-4 text-xs font-semibold uppercase tracking-[0.35px] text-brand transition-colors hover:bg-brand-shade"
            >
              <PlusIcon className="size-4" />
              Add Tags
            </button>
            <button
              type="button"
              aria-label="Clear tags"
              onClick={() => setFieldValue('tags', [])}
              className="flex size-11 shrink-0 items-center justify-center rounded-[10px] border border-[rgba(70,85,111,0.25)] text-ink-secondary transition-colors hover:text-[#ef4444]"
            >
              <TrashIcon className="size-5" />
            </button>
          </div>
          {values.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {values.tags.map((t) => (
                <span
                  key={t}
                  className="flex items-center gap-1.5 rounded-full bg-brand-shade px-3 py-1 text-xs font-medium text-brand"
                >
                  {t}
                  <button
                    type="button"
                    aria-label={`Remove ${t}`}
                    onClick={() => removeTag(t)}
                    className="text-brand/60 transition-colors hover:text-brand"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Description */}
        <label className="flex flex-col gap-1.5">
          <Label>Task Description</Label>
          <textarea
            name="description"
            rows={4}
            placeholder="Provide comprehensive details, prerequisites, and expected outcomes for this task..."
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            className={TEXTAREA}
          />
        </label>

        {/* Attachments */}
        <div className="flex flex-col gap-2">
          <Label>Attachments</Label>
          <label
            className={[
              'flex flex-col items-center justify-center rounded-lg border border-dashed border-[#cbd5e1] bg-white px-6 py-8 text-center transition-colors',
              uploading ? 'cursor-wait opacity-70' : 'cursor-pointer hover:border-brand/50',
            ].join(' ')}
          >
            <ImagePlaceholderIcon className="size-12 text-[#94a3b8]" />
            <p className="pt-4 text-sm">
              {uploading ? (
                <span className="font-semibold text-brand">Uploading…</span>
              ) : (
                <>
                  <span className="font-semibold text-brand">Upload a file</span>
                  <span className="text-[#475569]"> or drag and drop</span>
                </>
              )}
            </p>
            <p className="text-xs text-[#64748b]">PNG, JPG, PDF up to 10MB</p>
            <input
              type="file"
              multiple
              accept=".png,.jpg,.jpeg,.pdf"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                handleFiles(e.target.files);
                e.target.value = ''; // allow re-selecting the same file
              }}
            />
          </label>

          {/* Uploaded files */}
          {values.attachments.length > 0 && (
            <ul className="flex flex-col gap-2">
              {values.attachments.map((a) => (
                <li
                  key={a.file_url}
                  className="flex items-center justify-between rounded-lg border border-[#f1f5f9] bg-[#f8fafc] px-4 py-2.5"
                >
                  <a
                    href={a.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 truncate text-sm text-ink hover:text-brand"
                  >
                    <span className="flex size-8 shrink-0 items-center justify-center rounded bg-white text-[10px] font-bold uppercase text-[#ef4444] shadow-card">
                      {a.file_type.slice(0, 3)}
                    </span>
                    <span className="truncate">{a.name}</span>
                  </a>
                  <button
                    type="button"
                    aria-label={`Remove ${a.name}`}
                    onClick={() => removeAttachment(a.file_url)}
                    className="shrink-0 text-ink-secondary transition-colors hover:text-[#ef4444]"
                  >
                    <TrashIcon className="size-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-6 border-t border-[#f1f5f9] pt-4">
          <button
            type="button"
            onClick={() => navigate(backPath, { state: backState })}
            className="text-sm font-semibold uppercase tracking-[0.35px] text-[#475569] transition-colors hover:text-ink"
          >
            CANCEL
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-brand px-6 py-2.5 text-sm font-semibold uppercase tracking-[0.35px] text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            <CreateTaskIcon className="h-[15px] w-[17px]" />
            {saving ? 'SAVING…' : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}
